import { io, Socket } from "socket.io-client";

export interface EvidenceEvent {
  company_id: string;
  integrator_adapter: string;
  retransmission_id: string;
  ws_name: string;
  imei: string;
  device_id: string;
  sent_at: string;
  response_at: string;
  http_status: number;
  integrator_ok: boolean;
  error: string;
  job_id: string;
  latency_ms: number;
  dry_run: boolean;
  reauthed: boolean;
  request: string;
  response: string;
  historical: boolean;
}

export interface EvidenceFilter {
  device_ids?: string[];
  company_ids?: string[];
  integrators?: string[];
  retransmission_ids?: string[];
  all?: boolean;
}

interface SubscribedEvidenceResponse {
  rooms: string[];
}

interface ServerToClientEvents {
  evidence: (ev: EvidenceEvent) => void;
  "subscribed:evidence": (response: SubscribedEvidenceResponse) => void;
}

interface ClientToServerEvents {
  "subscribe:evidence": (filter: EvidenceFilter) => void;
  "unsubscribe:evidence": (filter: EvidenceFilter) => void;
}

const EVIDENCE_SOCKET_URL =
  import.meta.env.VITE_DUMAX_EVIDENCE_SOCKET_URL ||
  "https://dumax-socket-oyni7keu3a-uc.a.run.app";

let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;

export const getEvidenceSocket = (): Socket<
  ServerToClientEvents,
  ClientToServerEvents
> => {
  if (!socket) {
    socket = io(EVIDENCE_SOCKET_URL, {
      transports: ["websocket"],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
  }
  return socket;
};

export const connectEvidenceSocket = (): Socket<
  ServerToClientEvents,
  ClientToServerEvents
> => {
  const socketInstance = getEvidenceSocket();
  if (!socketInstance.connected) {
    socketInstance.connect();
  }
  return socketInstance;
};

export const disconnectEvidenceSocket = (): void => {
  if (socket?.connected) {
    socket.disconnect();
  }
};

export const isEvidenceSocketConnected = (): boolean => {
  return socket?.connected ?? false;
};

export const hasAnyFilter = (filter: EvidenceFilter): boolean => {
  return Boolean(
    filter.all ||
      filter.device_ids?.length ||
      filter.company_ids?.length ||
      filter.integrators?.length ||
      filter.retransmission_ids?.length
  );
};

export const subscribeToEvidence = (filter: EvidenceFilter): void => {
  if (!hasAnyFilter(filter)) return;
  const socketInstance = getEvidenceSocket();
  socketInstance.emit("subscribe:evidence", filter);
};

export const unsubscribeFromEvidence = (filter: EvidenceFilter): void => {
  if (!hasAnyFilter(filter)) return;
  const socketInstance = getEvidenceSocket();
  if (!socketInstance.connected) return;
  socketInstance.emit("unsubscribe:evidence", filter);
};
